/**
 * Страница управления Telegram каналами и ботами
 * Современный тёмный дизайн
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import AnimatedCard from '../components/AnimatedCard';
import MagneticButton from '../components/MagneticButton';
import { showToast, initToast } from '../components/Toast';
import { API_URL } from '../utils/api.js';

export default function Telegram() {
  const [channels, setChannels] = useState([]);
  const [botTokens, setBotTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChannelForm, setShowChannelForm] = useState(false);
  const [showBotForm, setShowBotForm] = useState(false);
  const [channelFormData, setChannelFormData] = useState({
    channel_username: '',
    bot_token_id: '',
    bot_token: '',
    is_target: true,
  });
  const [botFormData, setBotFormData] = useState({
    name: '',
    token: '',
  });

  useEffect(() => {
    initToast();
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchChannels(), fetchBotTokens()]);
    } catch (error) {
      console.error('Ошибка при загрузке данных:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChannels = async () => {
    try {
      const res = await axios.get(`${API_URL}/channels`);
      setChannels(res.data.channels || []);
    } catch (error) {
      console.error('Ошибка при загрузке каналов:', error);
    }
  };

  const fetchBotTokens = async () => {
    try {
      const res = await axios.get(`${API_URL}/bot-tokens`);
      setBotTokens(res.data.botTokens || []);
    } catch (error) {
      console.error('Ошибка при загрузке ботов:', error);
    }
  };

  const handleChannelSubmit = async (e) => {
    e.preventDefault();
    try {
      const { bot_token_id, bot_token, ...rest } = channelFormData;
      
      let finalBotToken = bot_token;
      if (bot_token_id && !bot_token) {
        const selectedBot = botTokens.find(b => b.id.toString() === bot_token_id);
        if (selectedBot) {
          finalBotToken = selectedBot.token;
        }
      }

      if (!finalBotToken) {
        showToast('⚠️ Выберите бота из списка или введите токен бота', 'warning');
        return;
      }

      const response = await axios.post(`${API_URL}/channels`, {
        ...rest,
        bot_token: finalBotToken,
      });
      
      if (response.data.error) {
        showToast(`❌ Ошибка: ${response.data.message || response.data.error}`, 'error');
        return;
      }
      
      showToast('✅ Канал успешно создан!', 'success');
      setShowChannelForm(false);
      setChannelFormData({ channel_username: '', bot_token_id: '', bot_token: '', is_target: true });
      fetchChannels();
    } catch (error) {
      console.error('Ошибка при создании канала:', error);
      alert(`❌ Ошибка: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleBotSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/bot-tokens`, botFormData);
      
      if (response.data.error) {
        alert(`❌ Ошибка: ${response.data.error}`);
        return;
      }
      
      alert('✅ Бот успешно добавлен!');
      setShowBotForm(false);
      setBotFormData({ name: '', token: '' });
      fetchBotTokens();
    } catch (error) {
      console.error('Ошибка при создании бота:', error);
      alert(`❌ Ошибка: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeleteChannel = async (id) => {
    if (!confirm('Удалить канал?')) return;
    try {
      await axios.delete(`${API_URL}/channels/${id}`);
      alert('✅ Канал удален!');
      fetchChannels();
    } catch (error) {
      alert(`❌ Ошибка: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDeleteBot = async (id) => {
    if (!confirm('Удалить бота?')) return;
    try {
      await axios.delete(`${API_URL}/bot-tokens/${id}`);
      alert('✅ Бот удален!');
      fetchBotTokens();
    } catch (error) {
      alert(`❌ Ошибка: ${error.response?.data?.message || error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Секция ботов */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white">🤖 Боты</h2>
            <p className="text-gray-400 text-sm">Управление Telegram ботами</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowBotForm(!showBotForm)}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium shadow-lg shadow-purple-500/30"
          >
            ➕ Добавить бота
          </motion.button>
        </div>

        <AnimatePresence>
          {showBotForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6 overflow-hidden"
            >
              <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
                <form onSubmit={handleBotSubmit} className="space-y-4">
                  <div>
                    <label className="block text-gray-300 text-sm mb-2">Название бота (опционально)</label>
                    <input
                      type="text"
                      value={botFormData.name}
                      onChange={(e) => setBotFormData({ ...botFormData, name: e.target.value })}
                      className="input"
                      placeholder="Мой бот"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-2">Bot Token *</label>
                    <input
                      type="text"
                      value={botFormData.token}
                      onChange={(e) => setBotFormData({ ...botFormData, token: e.target.value })}
                      className="input"
                      placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Токен от @BotFather в Telegram
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-medium"
                    >
                      Создать
                    </motion.button>
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowBotForm(false)}
                      className="px-6 py-2 bg-white/10 text-white rounded-xl font-medium"
                    >
                      Отмена
                    </motion.button>
                  </div>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="space-y-3">
          {botTokens.length === 0 ? (
            <div className="p-8 bg-white/5 rounded-2xl border border-white/10 text-center">
              <span className="text-4xl mb-3 block">🤖</span>
              <p className="text-gray-400">Нет добавленных ботов</p>
              <p className="text-gray-500 text-sm mt-1">Добавьте бота для публикации в каналы</p>
            </div>
          ) : (
            botTokens.map((bot, i) => (
              <motion.div
                key={bot.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="p-4 bg-white/5 rounded-2xl border border-white/10 hover:border-purple-500/30 transition-all"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                      <span className="text-2xl">🤖</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">
                        {bot.name || `@${bot.bot_username || 'Бот'}`}
                      </h3>
                      <div className="text-sm text-gray-400 space-y-0.5">
                        {bot.bot_username && <p>@{bot.bot_username}</p>}
                        <p className="text-xs text-gray-500">ID: {bot.bot_id || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDeleteBot(bot.id)}
                    className="px-4 py-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-all"
                  >
                    🗑️ Удалить
                  </motion.button>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Секция каналов */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white">📢 Каналы</h2>
            <p className="text-gray-400 text-sm">Каналы для публикации контента</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowChannelForm(!showChannelForm)}
            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30"
          >
            ➕ Добавить канал
          </motion.button>
        </div>

        <AnimatePresence>
          {showChannelForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6 overflow-hidden"
            >
              <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
                <form onSubmit={handleChannelSubmit} className="space-y-4">
                  <div>
                    <label className="block text-gray-300 text-sm mb-2">Username канала *</label>
                    <input
                      type="text"
                      value={channelFormData.channel_username}
                      onChange={(e) => setChannelFormData({ ...channelFormData, channel_username: e.target.value })}
                      className="input"
                      placeholder="@channel"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-2">Бот-администратор *</label>
                    {botTokens.length > 0 ? (
                      <select
                        value={channelFormData.bot_token_id}
                        onChange={(e) => setChannelFormData({ ...channelFormData, bot_token_id: e.target.value, bot_token: '' })}
                        className="input mb-2"
                      >
                        <option value="">-- Выберите бота --</option>
                        {botTokens.map((bot) => (
                          <option key={bot.id} value={bot.id}>
                            {bot.name || `@${bot.bot_username || 'Бот'}`}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-sm text-yellow-400 mb-2 p-3 bg-yellow-500/10 rounded-xl border border-yellow-500/30">
                        ⚠️ Сначала добавьте бота выше
                      </p>
                    )}
                    <input
                      type="text"
                      value={channelFormData.bot_token}
                      onChange={(e) => setChannelFormData({ ...channelFormData, bot_token: e.target.value })}
                      className="input"
                      placeholder="Или введите токен вручную"
                      disabled={!!channelFormData.bot_token_id}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Бот должен быть администратором канала с правами на публикацию
                    </p>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                    <input
                      type="checkbox"
                      id="is_target"
                      checked={channelFormData.is_target}
                      onChange={(e) => setChannelFormData({ ...channelFormData, is_target: e.target.checked })}
                      className="w-5 h-5 rounded accent-purple-500"
                    />
                    <label htmlFor="is_target" className="text-gray-300">Целевой канал для публикации</label>
                  </div>
                  <div className="flex gap-2">
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-medium"
                    >
                      Создать
                    </motion.button>
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowChannelForm(false)}
                      className="px-6 py-2 bg-white/10 text-white rounded-xl font-medium"
                    >
                      Отмена
                    </motion.button>
                  </div>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="space-y-3">
          {channels.length === 0 ? (
            <div className="p-8 bg-white/5 rounded-2xl border border-white/10 text-center">
              <span className="text-4xl mb-3 block">📢</span>
              <p className="text-gray-400">Нет добавленных каналов</p>
              <p className="text-gray-500 text-sm mt-1">Добавьте канал для публикации контента</p>
            </div>
          ) : (
            channels.map((channel, i) => (
              <motion.div
                key={channel.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="p-4 bg-white/5 rounded-2xl border border-white/10 hover:border-blue-500/30 transition-all"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                      <span className="text-2xl">📢</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">
                        {channel.channel_title || channel.channel_username}
                      </h3>
                      <div className="text-sm text-gray-400 space-y-0.5">
                        <p>{channel.channel_username}</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className={`badge ${channel.is_target ? 'badge-green' : 'badge-yellow'}`}>
                            {channel.is_target ? '✅ Целевой' : '📋 Мониторинг'}
                          </span>
                          <span className={`badge ${channel.bot_token ? 'badge-blue' : 'badge-red'}`}>
                            {channel.bot_token ? '🤖 Бот настроен' : '⚠️ Нет бота'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDeleteChannel(channel.id)}
                    className="px-4 py-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-all"
                  >
                    🗑️ Удалить
                  </motion.button>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
